/** @format */

// src/pages/ProductsPage.jsx
import {
  faAward,
  faBookOpen,
  faBoxArchive,
  faBoxOpen,
  faBuilding,
  faEye,
  faFilter,
  faTimes,
  faToggleOn,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { ThemeProvider } from '../../../../theme';
import ProductForm from './ProductForm'; // Same here
import ProductList from './ProductList'; // Assuming ProductList is in the same dir or adjust path

import { api } from '../../../../api.ts';
import { formatApiError } from './productName';

const PRODUCT_LIST_SEARCH_DEBOUNCE_MS = 400;

function FilterButton({
  type,
  icon,
  label,
  activeFilter,
  filters,
  handleFilterClick,
  onCycle,
  onRemove,
}) {
  const isActive = activeFilter === type;
  const currentValue = filters[type];
  const isRemovable =
    (type === 'typology' || type === 'brand' || type === 'sub_category') &&
    currentValue !== null;

  let displayText = label;
  if (currentValue !== null) {
    if (type === 'is_active') {
      displayText = `Actif : ${currentValue ? 'Oui' : 'Non'}`;
    } else if (type === 'is_validated') {
      displayText = `Validé : ${currentValue ? 'Oui' : 'Non'}`;
    } else if (type === 'is_kit') {
      displayText = `Kit : ${currentValue ? 'Oui' : 'Non'}`;
    } else if (type === 'typology' && typeof currentValue === 'object') {
      displayText = `Typologie : ${currentValue.name}`;
    } else if (type === 'sub_category' && typeof currentValue === 'object') {
      displayText = `Sous-catégorie : ${currentValue.name}`;
    } else if (type === 'brand' && typeof currentValue === 'object') {
      displayText = `Marque : ${currentValue.name}`;
    } else if (type === 'is_visible_in_offer') {
      displayText = `Visible dans les offres : ${currentValue ? 'Oui' : 'Non'}`;
    } else if (type === 'is_archived') {
      displayText = `Archivé : ${currentValue ? 'Oui' : 'Non'}`;
    } else if (type === 'is_visible_in_catalog') {
      displayText = `Visible dans le catalogue : ${
        currentValue ? 'Oui' : 'Non'
      }`;
    }
  }

  return (
    <Button
      color="secondary"
      variant={currentValue !== null ? 'contained' : 'outlined'}
      onClick={() => {
        if (
          type === 'is_active' ||
          type === 'is_validated' ||
          type === 'is_kit' ||
          type === 'is_visible_in_offer' ||
          type === 'is_archived' ||
          type === 'is_visible_in_catalog'
        ) {
          onCycle(type);
        } else {
          handleFilterClick(type);
        }
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        height: 40,
        position: 'relative',
        ...(isRemovable && { pr: 3 }),
      }}
    >
      <FontAwesomeIcon icon={icon} size="lg" />
      <Box component="span" sx={{ ml: 1 }}>
        {displayText}
      </Box>
      {isRemovable && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(type);
          }}
          sx={{
            ml: 1,
            color: 'inherit',
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            p: 0.5,
          }}
        >
          <FontAwesomeIcon icon={faTimes} size="sm" />
        </IconButton>
      )}
    </Button>
  );
}

FilterButton.propTypes = {
  type: PropTypes.oneOf([
    'typology',
    'is_active',
    'is_validated',
    'brand',
    'is_visible_in_offer',
    'is_archived',
    'is_visible_in_catalog',
  ]).isRequired,
  icon: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      iconName: PropTypes.string,
      prefix: PropTypes.string,
    }),
  ]).isRequired,
  label: PropTypes.string.isRequired,
  activeFilter: PropTypes.oneOf([
    'typology',
    'is_active',
    'is_validated',
    'brand',
    'is_visible_in_offer',
    'is_archived',
    'is_visible_in_catalog',
  ]).isRequired,
  filters: PropTypes.shape({
    typology: PropTypes.shape({}),
    is_active: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
    is_validated: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf([null]),
    ]),
    brand: PropTypes.shape({}),
    is_visible_in_offer: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf([null]),
    ]),
    is_archived: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf([null])]),
    is_visible_in_catalog: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf([null]),
    ]),
  }).isRequired,
  handleFilterClick: PropTypes.func.isRequired,
  onCycle: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formSubmitLoading, setFormSubmitLoading] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDeleteId, setProductToDeleteId] = useState(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Replace the showTypologyRow state with a more generic filter UI state
  const [activeFilter, setActiveFilter] = useState(null); // 'typology', 'active', 'validated', 'brand'

  // Replace selectedTypology with a filters object
  const [filters, setFilters] = useState({
    typology: null,
    is_active: null,
    is_validated: null,
    is_visible_in_offer: null,
    is_archived: false,
    is_kit: null,
    is_visible_in_catalog: null,
    brand: null,
    sub_category: null,
  });

  const [typologies, setTypologies] = useState([]);
  // Fetch brands on mount alongside typologies
  const [brands, setBrands] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [subCategories, setSubCategories] = useState([]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPaginationModel((prev) =>
        prev.page === 0 ? prev : { ...prev, page: 0 },
      );
    }, PRODUCT_LIST_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    Promise.all([
      api.get('/products/v2/typologies/?page_size=1000'),
      api.get('/products/v2/brands/?page_size=1000'),
    ]).then(([typologiesRes, brandsRes]) => {
      setTypologies(typologiesRes.data.results || []);
      setBrands(brandsRes.data.results || []);
    });
  }, []);

  useEffect(() => {
    api.get('/products/v2/sub-categories/?page_size=1000').then((res) => {
      setSubCategories(res.data.results || []);
    });
  }, []);

  const loadProducts = useCallback(
    (options = {}) => {
      const { signal } = options;
      setIsLoading(true);
      setError(null);
      const params = [];
      if (debouncedSearch) {
        params.push(`search=${encodeURIComponent(debouncedSearch)}`);
      }
      if (filters.typology && filters.typology.slug) {
        params.push(`typology=${filters.typology.slug}`);
      }
      if (filters.is_active !== null) {
        params.push(`is_active=${filters.is_active}`);
      }
      if (filters.is_validated !== null) {
        params.push(`is_validated=${filters.is_validated}`);
      }
      if (filters.brand && filters.brand.id) {
        params.push(`brand=${filters.brand.id}`);
      }
      if (filters.sub_category && filters.sub_category.slug) {
        params.push(`sub_categories=${filters.sub_category.slug}`);
      }
      if (filters.is_visible_in_offer !== null) {
        params.push(`is_visible_in_offer=${filters.is_visible_in_offer}`);
      }
      if (filters.is_archived !== null) {
        params.push(`is_archived=${filters.is_archived}`);
      }
      if (filters.is_kit !== null) {
        params.push(`has_kit_lines=${filters.is_kit}`);
      }
      if (filters.is_visible_in_catalog !== null) {
        params.push(`is_visible_in_catalog=${filters.is_visible_in_catalog}`);
      }
      params.push(`page=${paginationModel.page + 1}`);
      params.push(`page_size=${paginationModel.pageSize}`);
      if (sortModel.length > 0) {
        const sort = sortModel[0];
        if (sort.field) {
          let backendField = sort.field;
          if (backendField === 'public_name')
            backendField = '_indexed_public_name';
          if (backendField === 'internal_name')
            backendField = '_indexed_internal_name';
          params.push(
            `ordering=${sort.sort === 'desc' ? '-' : ''}${backendField}`,
          );
        }
      }
      const url = `/products/v2/admin-products/?${params.join('&')}`;
      const requestConfig = signal ? { signal } : {};
      api
        .get(url, {}, {}, 'json', requestConfig)
        .then((response) => {
          setProducts(response.data.results || []);
          setRowCount(response.data.count || 0);
          setIsLoading(false);
        })
        .catch((err) => {
          if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
            return;
          }
          const errorMsg =
            err.response?.data?.detail ||
            err.message ||
            'Failed to load products.';
          setError(`list-load: ${errorMsg}`);
          console.error('Load products error:', err);
          setIsLoading(false);
        });
    },
    [
      paginationModel.page,
      paginationModel.pageSize,
      debouncedSearch,
      filters,
      sortModel,
    ],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadProducts({ signal: controller.signal });
    return () => controller.abort();
  }, [loadProducts]);

  const handleOpenForm = async (product = null) => {
    setError(null); // Clear general page error
    if (product && product.id) {
      setFormSubmitLoading(true);
      try {
        const response = await api.get(
          `/products/v2/admin-products/${product.id}/`,
        );
        setEditingProduct({ ...response.data });
        setIsFormOpen(true);
      } catch (err) {
        setError('Erreur lors du chargement des détails du produit.');
      } finally {
        setFormSubmitLoading(false);
      }
    } else {
      setEditingProduct(product);
      setIsFormOpen(true);
    }
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
  };

  // onSave in ProductForm will call this
  const handleSaveProduct = async (
    productData,
    imageFile,
    productId = null,
    dataSheetFormData = null,
  ) => {
    setFormSubmitLoading(true);
    try {
      let savedProduct;

      // Step 1: Always update/create the product data
      if (productId) {
        const response = await api.patch(
          `/products/v2/admin-products/${productId}/`,
          {},
          productData,
        );
        savedProduct = response.data;
      } else {
        const response = await api.post(
          '/products/v2/admin-products/',
          {},
          productData,
        );
        savedProduct = response.data;
      }

      // Step 2: Update image IF CHANGED
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);
        const imageResponse = await api.patch(
          `/products/v2/admin-products/${savedProduct.id}/`,
          {},
          imageFormData,
          { 'Content-Type': 'multipart/form-data' },
        );
        savedProduct = { ...savedProduct, ...imageResponse.data };
      }

      // Step 3: Update data sheets IF CHANGED
      if (
        dataSheetFormData &&
        Array.from(dataSheetFormData.entries()).length > 0
      ) {
        const uploadResponse = await api.post(
          `/products/v2/admin-products/${savedProduct.id}/upload_data_sheets/`,
          {},
          dataSheetFormData,
          { 'Content-Type': 'multipart/form-data' },
        );
        savedProduct = uploadResponse.data;
      }

      handleCloseForm();
      loadProducts();
      return savedProduct;
    } finally {
      setFormSubmitLoading(false);
    }
  };

  const handleDuplicateProduct = async (product) => {
    setFormSubmitLoading(true);
    try {
      await api.post(
        `/products/v2/admin-products/${product.id}/duplicate/`,
        {},
      );
      loadProducts();
    } catch (err) {
      setError('Erreur lors de la duplication du produit.');
      console.error('Duplicate product error:', err);
    } finally {
      setFormSubmitLoading(false);
    }
  };

  const handleSyncProductToOdoo = async (product) => {
    setFormSubmitLoading(true);
    try {
      const response = await api.post(
        `/products/v2/admin-products/${product.id}/sync_to_odoo/`,
        {},
        {},
      );
      // Update the product in the list with the new odoo_id and odoo_bom_id
      setProducts((prevProducts) =>
        prevProducts.map((p) =>
          p.id === product.id
            ? {
                ...p,
                odoo_id: response.data.odoo_id,
                odoo_bom_id: response.data.odoo_bom_id,
              }
            : p,
        ),
      );
      // Also update editingProduct if it's the same product
      if (editingProduct && editingProduct.id === product.id) {
        setEditingProduct((prev) => ({
          ...prev,
          odoo_id: response.data.odoo_id,
          odoo_bom_id: response.data.odoo_bom_id,
        }));
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          'Erreur lors de la synchronisation avec Odoo.',
      );
      console.error('Sync product to Odoo error:', err);
    } finally {
      setFormSubmitLoading(false);
    }
  };

  const openDeleteDialog = (id) => {
    setProductToDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setProductToDeleteId(null);
    setDeleteDialogOpen(false);
  };

  const handleDeleteProduct = async () => {
    if (!productToDeleteId) return;
    setFormSubmitLoading(true); // Re-use for delete operation's loading state
    setError(null);
    try {
      // Using api.ts
      await api.delete(`/products/v2/products/${productToDeleteId}/`);
      closeDeleteDialog();
      loadProducts(); // Refresh list
    } catch (err) {
      const errorMsg =
        err.response?.data?.detail ||
        err.message ||
        'Failed to delete product.';
      setError(`delete: ${errorMsg}`);
      console.error('Delete product error:', err);
    } finally {
      setFormSubmitLoading(false);
    }
  };

  // Filter products by public_name (case-insensitive)
  /* const filteredProducts = products.filter((product) =>
    product.public_name?.toLowerCase().includes(searchQuery.toLowerCase()),
  ); */

  // UI handlers for typology filter
  const handleFilterButtonClick = () => {
    setActiveFilter((prev) => (prev === 'typology' ? null : 'typology'));
  };
  const handleTypologySelect = (typology) => {
    setFilters((prev) => ({ ...prev, typology }));
    setActiveFilter(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page
  };
  const handleRemoveTypology = () => {
    setFilters((prev) => ({ ...prev, typology: null }));
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // Reset to first page
  };

  // New filter handlers
  const handleFilterClick = (filterType) => {
    setActiveFilter((prev) => (prev === filterType ? null : filterType));
  };

  const handleFilterChange = (type, value) => {
    setFilters((prev) => ({ ...prev, [type]: value }));
    setActiveFilter(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRemoveFilter = (type) => {
    setFilters((prev) => ({ ...prev, [type]: null }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleCycleBooleanFilter = (type) => {
    setFilters((prev) => {
      const current = prev[type];
      let next;
      if (current === null) next = true;
      else if (current === true) next = false;
      else next = null;
      return { ...prev, [type]: next };
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Inline edit handler for DataGrid
  const handleProcessRowUpdate = async (newRow, oldRow) => {
    try {
      // Only send changed fields
      const changedFields = {};
      if (newRow.is_active !== oldRow.is_active)
        changedFields.is_active = newRow.is_active;
      if (newRow.is_validated !== oldRow.is_validated)
        changedFields.is_validated = newRow.is_validated;
      if (newRow.is_visible_in_offer !== oldRow.is_visible_in_offer)
        changedFields.is_visible_in_offer = newRow.is_visible_in_offer;
      if (newRow.is_archived !== oldRow.is_archived)
        changedFields.is_archived = newRow.is_archived;
      if (Object.keys(changedFields).length === 0) return oldRow;
      await api.patch(
        `/products/v2/admin-products/${newRow.id}/`,
        {},
        changedFields,
      );
      // Optionally, reload the list
      loadProducts();
      return { ...oldRow, ...changedFields };
    } catch (err) {
      setError(
        formatApiError(err, 'Erreur lors de la mise à jour du produit.'),
      );
      return oldRow;
    }
  };

  return (
    <ThemeProvider>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Typography variant="h4" gutterBottom component="div">
          Interface de gestion des produits
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              position: 'relative',
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
            >
              Ajouter un nouveau produit
            </Button>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Rechercher un produit"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 270 }}
            />
            <FilterButton
              type="typology"
              icon={faFilter}
              label="Typologie"
              activeFilter={activeFilter}
              filters={filters}
              handleFilterClick={handleFilterClick}
              onCycle={handleCycleBooleanFilter}
              onRemove={handleRemoveFilter}
            />
            <FilterButton
              type="sub_category"
              icon={faFilter}
              label="Sous-catégorie"
              activeFilter={activeFilter}
              filters={filters}
              handleFilterClick={handleFilterClick}
              onCycle={handleCycleBooleanFilter}
              onRemove={handleRemoveFilter}
            />
            <FilterButton
              type="brand"
              icon={faBuilding}
              label="Marque"
              activeFilter={activeFilter}
              filters={filters}
              handleFilterClick={handleFilterClick}
              onCycle={handleCycleBooleanFilter}
              onRemove={handleRemoveFilter}
            />
            <FilterButton
              type="is_kit"
              icon={faBoxOpen}
              label="Kit ?"
              activeFilter={activeFilter}
              filters={filters}
              handleFilterClick={handleFilterClick}
              onCycle={handleCycleBooleanFilter}
            />
            <FilterButton
              type="is_active"
              icon={faToggleOn}
              label="Actif ?"
              activeFilter={activeFilter}
              filters={filters}
              handleFilterClick={handleFilterClick}
              onCycle={handleCycleBooleanFilter}
            />
            <FilterButton
              type="is_validated"
              icon={faAward}
              label="Validé ?"
              activeFilter={activeFilter}
              filters={filters}
              handleFilterClick={handleFilterClick}
              onCycle={handleCycleBooleanFilter}
            />
            <FilterButton
              type="is_visible_in_offer"
              icon={faEye}
              label="Visible dans les offres ?"
              activeFilter={activeFilter}
              filters={filters}
              handleFilterClick={handleFilterClick}
              onCycle={handleCycleBooleanFilter}
            />
            <FilterButton
              type="is_visible_in_catalog"
              icon={faBookOpen}
              label="Visible dans le catalogue ?"
              activeFilter={activeFilter}
              filters={filters}
              handleFilterClick={handleFilterClick}
              onCycle={handleCycleBooleanFilter}
            />
            <FilterButton
              type="is_archived"
              icon={faBoxArchive}
              label="Archivé ?"
              activeFilter={activeFilter}
              filters={filters}
              handleFilterClick={handleFilterClick}
              onCycle={handleCycleBooleanFilter}
            />
            <Box sx={{ flex: 1 }} />
          </Box>
        </Box>

        {/* Filter sections */}
        {activeFilter && (
          <Paper sx={{ mb: 2, p: 2 }}>
            {activeFilter === 'typology' && (
              <Box sx={{ width: '100%' }}>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  {typologies
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .slice(0, Math.ceil(typologies.length / 2))
                    .map((typology) => (
                      <Button
                        key={typology.id}
                        variant="outlined"
                        onClick={() => handleTypologySelect(typology)}
                        sx={{
                          height: 50,
                          textTransform: 'none',
                          flex: 1,
                          width: '100%',
                        }}
                        startIcon={
                          <FontAwesomeIcon
                            icon={typology.wizard_icon}
                            size="lg"
                          />
                        }
                      >
                        {typology.name}
                      </Button>
                    ))}
                </Stack>
                <Stack direction="row" spacing={1}>
                  {typologies
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .slice(Math.ceil(typologies.length / 2))
                    .map((typology) => (
                      <Button
                        key={typology.id}
                        variant="outlined"
                        onClick={() => handleTypologySelect(typology)}
                        sx={{ height: 50, textTransform: 'none', flex: 1 }}
                        startIcon={
                          <FontAwesomeIcon
                            icon={typology.wizard_icon}
                            size="lg"
                          />
                        }
                      >
                        {typology.name}
                      </Button>
                    ))}
                </Stack>
              </Box>
            )}

            {activeFilter === 'is_active' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={
                    filters.is_active === true ? 'contained' : 'outlined'
                  }
                  onClick={() => handleFilterChange('is_active', true)}
                >
                  Actif
                </Button>
                <Button
                  variant={
                    filters.is_active === false ? 'contained' : 'outlined'
                  }
                  onClick={() => handleFilterChange('is_active', false)}
                >
                  Inactif
                </Button>
              </Box>
            )}

            {activeFilter === 'is_validated' && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={
                    filters.is_validated === true ? 'contained' : 'outlined'
                  }
                  onClick={() => handleFilterChange('is_validated', true)}
                >
                  Validé
                </Button>
                <Button
                  variant={
                    filters.is_validated === false ? 'contained' : 'outlined'
                  }
                  onClick={() => handleFilterChange('is_validated', false)}
                >
                  Non validé
                </Button>
              </Box>
            )}

            {activeFilter === 'brand' && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {brands
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((brand) => (
                    <Button
                      key={brand.id}
                      variant={
                        filters.brand?.id === brand.id
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => handleFilterChange('brand', brand)}
                    >
                      {brand.name}
                    </Button>
                  ))}
              </Box>
            )}

            {activeFilter === 'sub_category' && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {subCategories
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((sc) => (
                    <Button
                      key={sc.slug}
                      variant={
                        filters.sub_category?.slug === sc.slug
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => handleFilterChange('sub_category', sc)}
                    >
                      {sc.name}
                    </Button>
                  ))}
              </Box>
            )}
          </Paper>
        )}

        <ProductList
          products={products}
          isLoading={isLoading}
          onEdit={handleOpenForm}
          onDelete={openDeleteDialog}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          rowCount={rowCount}
          onProcessRowUpdate={handleProcessRowUpdate}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
        />

        {isFormOpen && (
          <ProductForm
            open={isFormOpen}
            onClose={handleCloseForm}
            onSave={handleSaveProduct}
            product={editingProduct}
            isLoading={formSubmitLoading}
            handleDuplicateProduct={handleDuplicateProduct}
            handleSyncProductToOdoo={handleSyncProductToOdoo}
          />
        )}

        <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this product?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} disabled={formSubmitLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteProduct}
              color="error"
              autoFocus
              disabled={formSubmitLoading}
            >
              {formSubmitLoading ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}

export default ProductsPage;
